import * as three from 'three';
import { Box } from './box';
import { Vec } from './vec';
import { Vec3Like, VecLike } from './vec-like';
import { Vec2 } from './vec2';
import { Vec3 } from './vec3';

const tmpV3A=new Vec3();
const tmpV2A=new Vec2();

export class Box3 extends three.Box3 implements Box<Vec3>{
	public declare min:Vec3;
	public declare max:Vec3;

	constructor(min=new Vec3(Infinity,Infinity,Infinity), max=new Vec3(-Infinity,-Infinity,-Infinity)){
		super(min,max);
	}

	public setEmpty(){
		this.makeEmpty();
		return this;
	}

	public fromArray(array:number[]){
		if(array[0]!==undefined)
			this.min.x=array[0];
		if(array[1]!==undefined)
			this.min.y=array[1];
		if(array[2]!==undefined)
			this.min.z=array[2];
		if(array[3]!==undefined)
			this.max.x=array[3];
		if(array[4]!==undefined)
			this.max.y=array[4];
		if(array[5]!==undefined)
			this.max.z=array[5];
		return this;
	}

	public toArray(){
		const out=<[number,number,number,number,number,number]>[0,0,0,0,0,0];
		this.min.toArray(out,0);
		this.max.toArray(out,3);
		return out;
	}

	public size(dim:'x'|'y'|'z'):number;
	public size():Vec3;
	public size<T extends Vec3>(out:T):T;
	public size(arg:Vec3|'x'|'y'|'z'=new Vec3()){
		if(typeof(arg)==='string')
			return this.max[arg]-this.min[arg];
		arg.x=this.max.x-this.min.x;
		arg.y=this.max.y-this.min.y;
		arg.z=this.max.z-this.min.z;
		return arg;
	}

	public minSize(){
		const x=this.max.x-this.min.x;
		const y=this.max.y-this.min.y;
		const z=this.max.z-this.min.z;
		if(z<x && z<y)
			return z;
		if(y<x && y<=z)
			return y;
		return x;
	}

	public maxSize(){
		const x=this.max.x-this.min.x;
		const y=this.max.y-this.min.y;
		const z=this.max.z-this.min.z;
		if(z>x && z>y)
			return z;
		if(y>x && y>=z)
			return y;
		return x;
	}

	public maxSide(){
		const x=this.max.x-this.min.x;
		const y=this.max.y-this.min.y;
		const z=this.max.z-this.min.z;
		if(z>x && z>y)
			return 'z';
		if(y>x && y>=z)
			return 'y';
		return 'x';
	}

	public center(dim:'x'|'y'|'z'):number;
	public center():Vec3;
	public center<T extends Vec3>(out:T):T;
	public center(arg:Vec3|'x'|'y'|'z'=new Vec3()){
		if(typeof(arg)==='string')
			return (this.max[arg]+this.min[arg])/2;
		arg.x=(this.max.x+this.min.x)/2;
		arg.y=(this.max.y+this.min.y)/2;
		arg.z=(this.max.z+this.min.z)/2;
		return arg;
	}

	public atParam(p:Vec3, out=p.clone()){
		return out.copy(this.min).mix(this.max,p);
	}

	public _paramOf<P extends Vec2, O extends Vec2>(p:P, out:O):O;
	public _paramOf<P extends Vec3, O extends Vec3>(p:P, out:O):O;
	public _paramOf<P extends Vec2|Vec3, O extends (P extends Vec2?Vec2:Vec3)>(p:P, out:O):O;
	public _paramOf(p:Vec, out:Vec){
		return out.applyFunc((_,axis)=>{
			const dim=this.max[axis]-this.min[axis];
			return dim===0?0:(p[axis]!-this.min[axis])/dim;
		});
	}

	public paramOf<T extends Vec2|Vec3>(p:T):T;
	public paramOf(p:Vec2|Vec3){
		return this._paramOf<Vec2|Vec3,Vec2|Vec3>(p,p.clone());
	}

	public volume(){
		return (this.max.x-this.min.x)*(this.max.y-this.min.y)*(this.max.z-this.min.z);
	}

	public override expandByPoint(point:VecLike){
		this.min.min(point);
		this.max.max(point);
		return this;
	}

	public expandByPoints(points:Iterable<VecLike>):this{
		for(const p of points)
			this.expandByPoint(p);
		return this;
	}

	public override union(that:{min:VecLike,max:VecLike}){
		this.min.min(that.min);
		this.max.max(that.max);
		return this;
	}

	public override containsPoint(p:VecLike):boolean{
		//inclusive on both sides, so is threejs's
		return (
			this.min.x<=p.x && p.x<=this.max.x
			&& this.min.y<=p.y && p.y<=this.max.y
			&& (p.z===undefined || (this.min.z<=p.z && p.z<=this.max.z))
		);
	}

	public containsPointXY(p:VecLike):boolean{
		//inclusive on both sides, so is threejs's
		return (
			this.min.x<=p.x && p.x<=this.max.x
			&& this.min.y<=p.y && p.y<=this.max.y
		);
	}

	public distToPointSq(p:VecLike){
		if(p.z===undefined)
			tmpV2A.copy(p).clamp(this.min,this.max).distToSq(p);
		return tmpV3A.copy(p).clamp(this.min,this.max).distToSq(p);
	}

	public distToPoint(p:VecLike){
		return Math.sqrt(this.distToPointSq(p))
	}

	public override intersectsBox(that:{min:VecLike,max:VecLike}){
		if(that.max.x<this.min.x || that.min.x>this.max.x)
			return false;
		if(that.max.y<this.min.y || that.min.y>this.max.y)
			return false;
		if(that.max.z!==undefined && that.min.z!==undefined && (that.max.z<this.min.z || that.min.z>this.max.z))
			return false;
		return true;
	}

	public atCorner(i:number){
		i%=8;
		if(i<0)
			i+=8;
		return new Vec3(
			this[i%3===0?'min':'max'].x,
			this[i%4<2?'min':'max'].y,
			this[i<4?'min':'max'].z,
		);
	}

	public *corners(out?:Vec3){
		if(out){
			yield out.set(this.min.x,this.min.y,this.min.z);
			yield out.set(this.max.x,this.min.y,this.min.z);
			yield out.set(this.max.x,this.max.y,this.min.z);
			yield out.set(this.min.x,this.max.y,this.min.z);

			yield out.set(this.min.x,this.min.y,this.max.z);
			yield out.set(this.max.x,this.min.y,this.max.z);
			yield out.set(this.max.x,this.max.y,this.max.z);
			yield out.set(this.min.x,this.max.y,this.max.z);
		}else{
			yield this.min.clone();
			yield new Vec3(this.max.x,this.min.y,this.min.z);
			yield new Vec3(this.max.x,this.max.y,this.min.z);
			yield new Vec3(this.min.x,this.max.y,this.min.z);

			yield new Vec3(this.min.x,this.min.y,this.max.z);
			yield new Vec3(this.max.x,this.min.y,this.max.z);
			yield this.max.clone();
			yield new Vec3(this.min.x,this.max.y,this.max.z);
		}
	}

	public distToBox(thatBox: three.Box3): Box3.DistToBox {
		// 1. Check for intersection
		if (this.intersectsBox(thatBox)) {
			return {distance: 0}; // Boxes are intersecting, so the closest distance is 0
		}

		// 2. If no intersection, calculate the closest distance

		// Create temporary vectors to avoid re-instantiating in a loop
		const closestPointOnThis = new three.Vector3();
		const closestPointOnThat = new three.Vector3();

		// Get the center of each box (optional, but a good starting point for clamping)
		const thisCenter = new three.Vector3();
		const thatCenter = new three.Vector3();
		this.getCenter(thisCenter);
		thatBox.getCenter(thatCenter);

		// Find the closest point on boxA to the center of thatBox
		this.clampPoint(thatCenter, closestPointOnThis);

		// Find the closest point on thatBox to the center of boxA
		thatBox.clampPoint(closestPointOnThis, closestPointOnThat);

		// The distance between these two clamped points is generally NOT the closest distance
		// when the boxes are not intersecting.

		// A more accurate method when not intersecting:
		// For each box, find the point on its surface that is closest to the other box.

		// To find the actual minimum distance between two non-intersecting AABBs (Axis-Aligned Bounding Boxes),
		// you need to calculate the distance along each axis and then combine them.

		let dx = 0;
		if (this.max.x < thatBox.min.x) {
			dx = thatBox.min.x - this.max.x;
		} else if (thatBox.max.x < this.min.x) {
			dx = this.min.x - thatBox.max.x;
		}

		let dy = 0;
		if (this.max.y < thatBox.min.y) {
			dy = thatBox.min.y - this.max.y;
		} else if (thatBox.max.y < this.min.y) {
			dy = this.min.y - thatBox.max.y;
		}

		let dz = 0;
		if (this.max.z < thatBox.min.z) {
			dz = thatBox.min.z - this.max.z;
		} else if (thatBox.max.z < this.min.z) {
			dz = this.min.z - thatBox.max.z;
		}

		return {
			distance: Math.sqrt(dx * dx + dy * dy + dz * dz),
			closestPointOnThis,
			closestPointOnThat
		};
	}
}

export namespace Box3{
	export function of(minx:number, miny:number, minz:number,maxx:number, maxy:number, maxz:number){
		return new Box3(new Vec3(minx,miny,minz),new Vec3(maxx,maxy,maxz));
	}

	export function fromArray(array:number[]){
		return new Box3(new Vec3(array[0],array[1],array[2]),new Vec3(array[3],array[4],array[5]));
	}

	export function fromPoints(points:Iterable<Vec3Like>){
		return new Box3().expandByPoints(points);
	}

	export interface DistToBox{
		distance:number;
		closestPointOnThis?:three.Vector3
		closestPointOnThat?:three.Vector3
	}
}
